"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, RefreshCw, Flame, Zap, Gauge, CircleCheck as CheckCircle2, ArrowRight, PhoneCall, ShieldAlert, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "repair",
  title: "Water Heater Repair",
  badge: "Emergency Triage",
  badgeVariant: "destructive" as const,
  icon: "wrench",
  description: "Rapid diagnosis and repair for leaking tanks, failed pilot lights, faulty valves, and sudden loss of hot water.",
  bullets: "Same-day troubleshooting; Thermostat and sensor testing; Valve and burner replacements",
  urgency: "Available 24/7 for active leaks",
  link: "/water-heater-repair/"
}, {
  id: "replacement",
  title: "Water Heater Replacement",
  badge: "Full Installation",
  badgeVariant: "default" as const,
  icon: "refresh",
  description: "Complete replacement service with proper capacity sizing, safe disconnect, and eco-friendly haul-away of old equipment.",
  bullets: "Code-compliant installation; Expansion tank check; Old unit removal included",
  urgency: "Recommended for units over 10 years",
  link: "/water-heater-replacement/"
}, {
  id: "tankless",
  title: "Tankless Water Heater",
  badge: "High Efficiency",
  badgeVariant: "secondary" as const,
  icon: "gauge",
  description: "On-demand hot water solutions engineered to eliminate standby energy loss and optimize tight mechanical room spaces.",
  bullets: "Continuous hot water supply; Lower monthly utility costs; Compact wall-mount design",
  urgency: "Rebates & high-efficiency options",
  link: "/tankless-water-heater-service/"
}, {
  id: "gas",
  title: "Gas Water Heater",
  badge: "Gas Certified",
  badgeVariant: "outline" as const,
  icon: "flame",
  description: "Specialized servicing for natural gas and propane systems, prioritizing safety, combustion venting, and thermal efficiency.",
  bullets: "Gas leak & thermocouple safety checks; Flue exhaust ventilation inspection; Pilot assembly tuning",
  urgency: "Precision combustion tuning",
  link: "/water-heater-repair/"
}, {
  id: "electric",
  title: "Electric Water Heater",
  badge: "Rapid Response",
  badgeVariant: "secondary" as const,
  icon: "zap",
  description: "Targeted electrical diagnostics to fix tripped breakers, burnt-out heating elements, and fluctuating bath temperatures.",
  bullets: "Dual heating element testing; Upper & lower thermostat sync; Electrical line integrity test",
  urgency: "Quick element & wire replacements",
  link: "/water-heater-repair/"
}];
export default function WaterHeater() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      case "refresh":
        return <RefreshCw className="size-6 text-primary" />;
      case "gauge":
        return <Gauge className="size-6 text-primary" />;
      case "flame":
        return <Flame className="size-6 text-primary" />;
      case "zap":
        return <Zap className="size-6 text-primary" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="waterheaterservicescluster" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold mb-4">
            <Sparkles className="size-3.5 text-accent" />
            <span>Jonesboro Emergency Plumber Expert Solutions</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground tracking-tight mb-4">
            Hot Water Heater Services
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
            Fast, reliable diagnostics and repairs for Jonesboro homes and businesses. We resolve cold showers, dangerous leaks, and system failures with same-day emergency response.
          </p>
        </div>

        {/* Triage Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {services.map((item, index) => {
          const bulletList = item.bullets.split("; ");
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] bg-card text-card-foreground rounded-xl overflow-hidden hover:border-primary/40">
                  <CardHeader className="gap-3 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-3 rounded-lg bg-secondary flex items-center justify-center">
                        {getIcon(item.icon)}
                      </div>
                      <Badge variant={item.badgeVariant} className="font-medium">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground mt-2">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-normal">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-4 flex-1">
                    <div className="rounded-lg bg-muted/50 p-3 mb-4 border border-border/50">
                      <div className="flex items-start gap-2">
                        <ShieldAlert className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-xs font-semibold text-foreground">
                          {item.urgency}
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {bulletList.map((bullet, bulletIdx) => <li key={bulletIdx} className="flex items-start gap-2">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                          <span className="text-foreground/90">{bullet}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border/60 bg-muted/20">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between font-semibold text-primary hover:text-primary hover:bg-secondary/80">
                      <Link to={item.link}>
                        <span>Inspect & Schedule</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}

          {/* Quick Support Call Card */}
          <motion.div data-index={5} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.4
        }} className="flex">
            <Card className="flex flex-col justify-between w-full border border-primary/30 bg-primary text-primary-foreground shadow-sm rounded-xl overflow-hidden">
              <CardHeader className="gap-3 pb-2">
                <div className="p-3 w-fit rounded-lg bg-primary-foreground/10 text-primary-foreground flex items-center justify-center">
                  <PhoneCall className="size-6" />
                </div>
                <CardTitle className="text-xl font-bold font-default text-primary-foreground mt-2">
                  Emergency Water Heater Leaks?
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 text-sm leading-normal">
                  Sudden rupture or scalding pressure buildup requires immediate attention before water damage spreads.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-2 pb-4">
                <div className="space-y-2 text-sm text-primary-foreground/90">
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>24/7 Local Jonesboro technicians ready</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>Immediate water shut-off guidance</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>Direct dispatch with fully loaded trucks</span>
                  </p>
                </div>
              </CardContent>

              <CardFooter className="pt-2 border-t border-primary-foreground/15">
                <Button asChild variant="secondary" size="default" className="w-full font-bold shadow-sm">
                  <Link to="/contact/">
                    <PhoneCall className="size-4 mr-1" />
                    Request Immediate Help
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Bottom CTA Block */}
        <div className="text-center pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="w-full sm:w-auto font-semibold px-8 py-6 text-base shadow-sm">
            <Link to="/water-heater/">
              <span>View Water Heater Services</span>
              <ArrowRight className="size-5 ml-1" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold px-6 py-6 text-base">
            <Link to="/contact/">
              Contact 24/7 Support
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}